import React, { useEffect, useRef, useId } from 'react';

const CMSV6_IP = '103.6.245.78';

// ── Monkey-patch (same pattern as CmsV6VideoPlayer) ────────────
let _patchRefs = 0;
let _origFetch = null;
let _origWS    = null;

function _installPatch() {
  _patchRefs++;
  if (_patchRefs > 1) return;
  _origFetch = window.fetch;
  _origWS    = window.WebSocket;

  window.fetch = function patchedFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url ?? '';
    if (url.includes(CMSV6_IP)) {
      try {
        const p       = new URL(url);
        const devIdno = p.searchParams.get('DevIDNO') || p.searchParams.get('devIdno') || '';
        const channel = p.searchParams.get('Channel') || p.searchParams.get('channel') || '';
        return _origFetch(`/api/cmsv6/meta?devIdno=${devIdno}&channel=${channel}`, init);
      } catch {
        return _origFetch.apply(this, arguments);
      }
    }
    return _origFetch.apply(this, arguments);
  };

  function PatchedWebSocket(url, protocols) {
    let target = url;
    if (typeof url === 'string' && url.includes(CMSV6_IP)) {
      try {
        const p      = new URL(url);
        const origin = window.location.origin
          .replace('https://', 'wss://')
          .replace('http://',  'ws://');
        // Encode the upstream path so the proxy forwards to /3/5 (VOD) vs /3/3 (live)
        const params = new URLSearchParams(p.searchParams);
        if (p.pathname && p.pathname !== '/') params.set('__wspath', p.pathname);
        target = `${origin}/ws/cmsv6?${params.toString()}`;
        console.log('[VodPatch] target:', target);
      } catch {}
    }
    return new _origWS(target, protocols);
  }
  PatchedWebSocket.prototype  = _origWS.prototype;
  PatchedWebSocket.CONNECTING = _origWS.CONNECTING;
  PatchedWebSocket.OPEN       = _origWS.OPEN;
  PatchedWebSocket.CLOSING    = _origWS.CLOSING;
  PatchedWebSocket.CLOSED     = _origWS.CLOSED;
  window.WebSocket = PatchedWebSocket;
}

function _removePatch() {
  _patchRefs = Math.max(0, _patchRefs - 1);
  if (_patchRefs > 0) return;
  if (_origFetch) { window.fetch     = _origFetch; _origFetch = null; }
  if (_origWS)    { window.WebSocket = _origWS;    _origWS    = null; }
}

// ── Script loader ─────────────────────────────────────────────
// Handles the case where CmsV6VideoPlayer already loaded the SDK.
let _scriptPromise = null;
function _ensureScript() {
  if (_scriptPromise) return _scriptPromise;
  if (window.Cmsv6Player) return (_scriptPromise = Promise.resolve());
  _scriptPromise = new Promise((resolve, reject) => {
    if (document.getElementById('cmsv6-sdk')) {
      // Tag exists (another module loaded it) — poll until Cmsv6Player appears.
      const deadline = Date.now() + 5000;
      const poll = () => {
        if (window.Cmsv6Player) { resolve(); return; }
        if (Date.now() > deadline) { reject(new Error('SDK timeout')); return; }
        setTimeout(poll, 100);
      };
      poll();
      return;
    }
    const s   = document.createElement('script');
    s.id      = 'cmsv6-sdk';
    s.src     = '/cmsv6player.min.js';
    s.onload  = resolve;
    s.onerror = () => reject(new Error('Failed to load /cmsv6player.min.js'));
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// ── Single-channel player cell ────────────────────────────────
// Owns one Cmsv6Player instance (1×1, VOD). When url is null, renders
// a black placeholder. Shares the module-level monkey-patch and script.
function SingleChannelPlayer({ url, channelIndex }) {
  const uid       = useId().replace(/:/g, '');
  const domId     = `cmsv6ch${channelIndex}-${uid}`;
  const wrapRef   = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (!url) return; // null → placeholder; no cleanup needed

    let cancelled = false;
    _installPatch();

    async function start() {
      try {
        await _ensureScript();
      } catch (err) {
        console.error(`[CH${channelIndex + 1}] SDK load failed:`, err.message);
        _removePatch();
        return;
      }

      const deadline = Date.now() + 4000;
      while (!window.Cmsv6Player && Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 100));
      }
      if (!window.Cmsv6Player) {
        console.error(`[CH${channelIndex + 1}] SDK not found after timeout`);
        _removePatch();
        return;
      }
      if (cancelled || !wrapRef.current) { _removePatch(); return; }

      const w = wrapRef.current.clientWidth  || 400;
      const h = wrapRef.current.clientHeight || 225;

      try {
        const player = new window.Cmsv6Player({
          domId,
          width: w, height: h,
          live:              false,
          enableMse:         true,
          hideToobar:        false,
          hideSoundBtn:      false,
          hideFullScreenBtn: false,
          hideShotBtn:       false,
          singleRowNumber:   1,
          singleColNumber:   1,
        });

        playerRef.current = player;
        player.setWindowNum(1);
        player.startVodM([url]);
      } catch (err) {
        console.error(`[CH${channelIndex + 1}] init failed:`, err.message);
        _removePatch();
      }
    }

    start();

    return () => {
      cancelled = true;
      const p = playerRef.current;
      if (p) {
        try { p.stopVodM();  } catch {}
        try { p.destory();   } catch {}  // SDK typo
        playerRef.current = null;
      }
      const el = document.getElementById(domId);
      if (el) el.innerHTML = '';
      _removePatch();
    };
  }, [url, domId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!url) {
    return (
      <div style={{ background:'#0a0a0a', display:'flex', alignItems:'center',
        justifyContent:'center', color:'#333', fontSize:11 }}>
        CH{channelIndex + 1} — No recording
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ width:'100%', height:'100%', background:'#000' }}>
      <div id={domId} style={{ width:'100%', height:'100%' }} />
    </div>
  );
}

// ── CmsV6VodPlayer ────────────────────────────────────────────
// Props:
//   vodUrls — 4-element array [ch0_url, ch1_url, ch2_url, ch3_url]
//             where null means no recording for that channel.
//
// Renders a 2×2 CSS grid. Each cell is an independent SingleChannelPlayer.
// This guarantees a stable 2×2 layout regardless of active channel count.
export default function CmsV6VodPlayer({ vodUrls }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr',
      width: '100%',
      height: '100%',
      gap: 2,
      background: '#000',
    }}>
      {[0, 1, 2, 3].map(ch => (
        <SingleChannelPlayer
          key={ch}
          channelIndex={ch}
          url={vodUrls?.[ch] || null}
        />
      ))}
    </div>
  );
}
